import { EventEmitter, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { map, Observable, of } from 'rxjs';

import { DateForPmis, Pmis3JsonHelper } from '@pmis3/utils';

import { DataAccessService } from '.';
import { IProfile, IRootModulePathMapping, RootModulePathMappings } from '../models';

@Injectable({
    providedIn: 'root',
})
export class ProfileAuthGuard implements CanActivate {
    profileChanged: EventEmitter<IProfile> = new EventEmitter<IProfile>();
    private _userProfiles: IProfile[] = [];
    get profiles() {
        return this._userProfiles;
    }
    private _selectedProfile: IProfile | undefined = undefined;
    get selectedProfile() {
        return this._selectedProfile;
    }
    hasModule(module: string) {
        return (
            this.selectedProfile?.modules
                ?.map((m) => m.toString().toLowerCase())
                .includes(module.toLowerCase()) ?? false
        );
    }
    setSelectedProfile(value: IProfile | undefined) {
        this._selectedProfile = value;
        const ianaTimeZoneId = value?.regionConfig?.ianaTimeZoneId ?? '';
        if (ianaTimeZoneId) {
            DateForPmis.setTimeZoneId(ianaTimeZoneId);
        }
        this.isGlobalAdmin = value?.roles?.includes('GlobalAdmin') ?? false;
        this.isProfileAdmin =
            value?.roles?.includes('ProfileAdmin') || this.isGlobalAdmin;
        this._storageProfileId = value?.id ?? '';
        this.profileChanged.emit(value);
    }
    get profileId() {
        return this.selectedProfile?.id ?? this._storageProfileId;
    }
    isGlobalAdmin = false;
    isProfileAdmin = false;

    private _storageProfileIdKey = 'pmis-profile-id';
    private set _storageProfileId(value: string) {
        if (!value) {
            localStorage.removeItem(this._storageProfileIdKey);
            return;
        }
        localStorage.setItem(this._storageProfileIdKey, value);
    }
    private get _storageProfileId() {
        return localStorage.getItem(this._storageProfileIdKey) ?? '';
    }

    constructor(
        private _router: Router,
        private _dataService: DataAccessService
    ) {}

    canActivate(_: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean | UrlTree> | Promise<boolean> {
        const url = state.url;
        if (this.selectedProfile) {
            return this._checkProfileRootUrl(url);
        }
        else {
            return this.getProfiles().pipe(
                map((r) => {
                    const profiles = r ?? [];
                    if (profiles.length === 1) {
                        this.setSelectedProfile(profiles[0]);
                        return this._checkProfileRootUrl(url);
                    } else if (profiles.length === 0) {
                        this.setSelectedProfile(undefined);
                        this._router.navigate(['/login']);
                        return false;
                    }
                    return this._router.createUrlTree(['/select-profile'], {
                        queryParams: { returnUrl: url }
                    });
                })
            );
        }
    }

    getProfiles(): Observable<IProfile[]> {
        if (this._userProfiles.length > 0) {
            return of(this._userProfiles);
        }
        return this._dataService.get('i', 'userprofile').pipe(
            map((r) => {
                Pmis3JsonHelper.CheckJsonObject(r);
                const profiles = (r?.items ?? []) as IProfile[];
                this._userProfiles = profiles;
                return profiles;
            })
        );
    }

    getProfileRootMenuItems(): IRootModulePathMapping[] {
        const modules = this.selectedProfile?.modules ?? [];
        const rootModules = modules.map((m) => RootModulePathMappings.getModuleByName(m))
                                .filter((m) => !!m);
        return rootModules
    }

    private _checkProfileRootUrl(url: string): boolean {
        if (url === '/' || url === '/home') {
            return true;
        }
        if (this.isGlobalAdmin) {
            return true;
        }
        const module = RootModulePathMappings.getModuleByPath(url);
        if (!module || !this.hasModule(module.name)) {
            this._router.navigate(['']);
            return false;
        }
        return true;
    }
}
